// @ts-expect-error incompatibility with node16 resolution
import {Dialog, Transition} from '@headlessui/react';
import {Link} from '@shopify/hydrogen';
import clsx from 'clsx';
import {Fragment, useCallback, useState} from 'react';
import type {SanityMenuLink, SanityNavigationDropdown} from '../../types';
import CloseIcon from '../icons/Close';
import MenuIcon from '../icons/Menu';
import getLocale from '../../hooks/getLocale';
import MobileNavigationDropdown from './MobileNavigationDropdown.client';
import LogoIcon from '../icons/Logo';
import {UserIconSolid} from '../icons/UserSolid';

type Props = {
  linksLeft?: SanityMenuLink[];
  backpacksDropdown?: SanityNavigationDropdown[];
  dropdowns: SanityNavigationDropdown[];
  countrySelect?: {
    title?: object;
    text?: object;
  };
};

export default function MobileNavigation({
  dropdowns,
  linksLeft,
  backpacksDropdown,
}: Props) {
  const [open, setOpen] = useState(false);

  const handleParentClose = () => setOpen(false);
  const handleParentOpen = () => setOpen(true);

  function MenuLinks(link: SanityMenuLink) {
    if (link._type === 'collectionGroup') {
      return (
        <div key={link._key} className={'h2 font-bold'}>
          <div className="linkTextNavigation hover:border-b-transparent">
            {link.linkTitle && typeof link.linkTitle === 'object'
              ? getLocale(link.linkTitle)
              : link.title}
          </div>
          <div className="my-1 ml-8 space-y-1">
            {link.collectionLinks?.map((collectionLink) => {
              if (!collectionLink.url) {
                return null;
              }
              return (
                <div key={collectionLink._id}>
                  <Link
                    className="linkTextNavigation relative inline-flex whitespace-nowrap"
                    onClick={handleParentClose}
                    to={collectionLink.url}
                  >
                    {collectionLink.linkTitle &&
                    typeof collectionLink.linkTitle === 'object'
                      ? getLocale(collectionLink.linkTitle)
                      : collectionLink.title}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (link._type === 'linkExternal') {
      return (
        <div className="h2 flex items-center font-bold" key={link._key}>
          <a
            className="linkTextNavigation relative whitespace-nowrap"
            href={link.url}
            onClick={handleParentClose}
            rel="noreferrer"
            target={link.newWindow ? '_blank' : '_self'}
          >
            {link.linkTitle && typeof link.linkTitle === 'object'
              ? getLocale(link.linkTitle)
              : link.title}
          </a>
        </div>
      );
    }
    if (link._type === 'linkInternal') {
      if (!link.url) {
        return null;
      }

      return (
        <div className="h2 flex items-center font-bold" key={link._key}>
          <Link
            className="linkTextNavigation relative whitespace-nowrap"
            onClick={handleParentClose}
            to={link.url}
          >
            {link.linkTitle && typeof link.linkTitle === 'object'
              ? getLocale(link.linkTitle)
              : link.title}
          </Link>
        </div>
      );
    }

    return null;
  }

  const renderBackpacksDropdown = useCallback(() => {
    if (!backpacksDropdown?.length) return null;
    return (
      <MobileNavigationDropdown
        // @ts-ignore
        closeParent={handleParentClose}
        // @ts-ignore
        dropdown={backpacksDropdown[0]}
        // @ts-ignore
        key={backpacksDropdown[0]._key}
      />
    );
  }, [backpacksDropdown]);

  const renderRight = useCallback(() => {
    return dropdowns?.map((dropdown) => {
      return (
        // @ts-ignore
        dropdown?.columns?.length > 0 && (
          <MobileNavigationDropdown
            // @ts-ignore
            closeParent={handleParentClose}
            // @ts-ignore
            dropdown={dropdown}
            // @ts-ignore
            key={dropdown._key}
          />
        )
      );
    });
  }, [dropdowns]);

  const actions = () => {
    return (
      <>
        <div
          className={clsx(
            'absolute right-[40px] flex h-full items-center md:right-[30px]', //
            'md:right-0 md:mr-4',
          )}
        >
          {/* Account */}
          <Link
            className={clsx([
              'mr-3 flex aspect-square h-[2rem] items-center rounded-sm bg-darkGray bg-opacity-0 p-2',
              'hover:bg-opacity-10',
            ])}
            to="/account"
          >
            <UserIconSolid />
          </Link>
        </div>
      </>
    );
  };

  return (
    <>
      <button
        className={clsx(
          'absolute right-0 flex h-header-sm items-center p-4 text-xs font-bold duration-200',
          'hover:opacity-50',
          'md:ml-4',
          'lg:hidden',
        )}
        onClick={handleParentOpen}
      >
        <MenuIcon />
      </button>

      <Transition show={open}>
        <Dialog onClose={handleParentClose}>
          {/* Panel */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Panel className="fixed bottom-0 left-0 right-0 top-0 z-50 h-full w-full overflow-y-auto bg-menu pb-40 text-black">
              {/* Header */}
              <header className="flex h-header-sm items-center justify-between bg-white pl-4 sm:pl-12">
                <Link
                  className="linkTextNavigation"
                  onClick={handleParentClose}
                  to="/"
                >
                  <LogoIcon className={clsx('h-auto w-full max-w-[80px]')} />
                </Link>
                {actions()}
                <button
                  className="-ml-4 h-header-sm p-4"
                  type="button"
                  onClick={handleParentClose}
                >
                  <CloseIcon />
                </button>
              </header>

              {/* Links */}
              <div className="mt-12 space-y-4 px-4 sm:px-12">
                <div className="space-y-2">
                  {linksLeft?.map(MenuLinks)}
                  {renderBackpacksDropdown()}
                </div>

                <div className="space-y-2">{renderRight()}</div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>
    </>
  );
}
